import {Box, IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';

import {useForm} from 'react-hook-form';
import {
  useAlphaCodeCheck,
  useDomainData,
  useGetEntityList,
} from '../../../hooks/Projects/useProjects';

import {useEffect, useMemo, useState} from 'react';
import {ISelectItem} from '../../../shared/types';
import {EntityItem, GetHcodeParams} from '../../../api/types/project.type';

import {useDispatch, useSelector} from 'react-redux';
import {setDomainData} from '../../../redux/slice/domainDataSlice';
import {
  setSelectedClient,
  setFolderPaths,
  setSelectedClientId,
  setProjectName,
  setAlphaCode,
  setFormDirty,
  setServerSelected,
} from '../../../redux/slice/projectFormSlice';

import {RootState} from '../../../redux/store';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {EditProjectTooltip} from './EditProjectTooltip';
import ArrToast from '../../common/arr-toast/ArrToast';
import {ToastType} from '../../../shared/constants';
import {ReactComponent as CloseToast} from '../../../../src/assets/images/close-toast.svg';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';

const CreateProjectHeader: React.FC = () => {
  const dispatch = useDispatch();

  const {
    projectName,
    alphaCode,
    initialAlphaCode,
    selectedClient,
    type,
    selectedClientId,
    projectGuid,
    projectIndexGuid,
  } = useSelector((state: RootState) => state.projectForm);
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );
  const entityId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.entityId
  );
  const [nameChanged, setNameChanged] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.Error);
  const [message, setMessage] = useState('');
  const [alphaValue, setAlphaValue] = useState('');
  const [enabled, setEnabled] = useState(false);

  const {data: entityList, isLoading} = useGetEntityList(2);
  const params: GetHcodeParams = {
    hcode: alphaValue,
    entityTypeId: 2,
    selectedEntityId: selectedClientId ? selectedClientId.toString() : '',
    // projectId: loginUserData.projectId.toString(),
  };

  const {data: alphaCodeCheck, isError} = useAlphaCodeCheck(params, enabled);

  const dropdownItems: ISelectItem[] = useMemo(() => {
    return (
      entityList?.map((item: EntityItem) => ({
        key: item.key.toString(),
        displayValue: item.value,
      })) ?? []
    );
  }, [entityList]);

  const selectedEntity =
    dropdownItems.find((item) => item.displayValue === selectedClient) || null;

  const selectedEntityId =
    dropdownItems.find((item) => Number(item.key) === selectedClientId) || null;

  const {data: domainData} = useDomainData(
    roleId === -2 && type != 'edit'
      ? (selectedClientId ?? 0)
      : Number(selectedEntity?.key)
  );
  useEffect(() => {
    if (domainData) {
      dispatch(setDomainData(domainData));
    }
  }, [domainData, dispatch]);

  useEffect(() => {
    if (entityId && roleId === -2 && type !== 'edit') {
      dispatch(setSelectedClientId(entityId));
      const selectClient =
        dropdownItems.find((item) => Number(item.key) === entityId) || null;

      dispatch(setSelectedClient(selectClient?.displayValue?.toString() ?? ''));
      if (selectClient) {
        const folderPath = `${selectClient?.displayValue}\\${projectName}`;

        dispatch(setFolderPaths({type: 'project', path: folderPath}));
        dispatch(setFolderPaths({type: 'ingestion', path: folderPath}));
        dispatch(setFolderPaths({type: 'production', path: folderPath}));
        setNameChanged(false);
      }
    }
  }, [entityId, dropdownItems]);

  const {
    register,
    handleSubmit,
    formState: {errors, isDirty},
    reset,
    getValues,
    setValue,
    setError,
    trigger,
    watch,
    clearErrors,
  } = useForm({
    mode: 'all',
    shouldUnregister: false,
    defaultValues: {
      projectName: projectName,
      alphaCode: alphaCode,
      projectGuid: projectGuid,
      projectIndexGuid: projectIndexGuid,
    },
  });

  useEffect(() => {
    dispatch(setFormDirty(isDirty));
  }, [isDirty]);

  const handleAlphaBlur = () => {
    const value = getValues('alphaCode')?.trim();
    setAlphaValue(value);

    if (!value) return;

    const isValidPattern = /^[A-Za-z0-9_ ]+$/.test(value);
    if (!isValidPattern) {
      return;
    }

    if (!selectedEntity?.key && !selectedClientId) {
      return;
    }
    if (initialAlphaCode === value?.trim()) {
      return;
    }
    setEnabled(true);
  };

  useEffect(() => {
    if (projectName && type === 'edit') {
      reset({
        projectName: projectName,
        alphaCode: alphaCode,
        projectGuid: projectGuid,
        projectIndexGuid: projectIndexGuid,
      });
    }
  }, [projectName, alphaCode, reset]);

  const onSubmit = () => {
    console.log('Project form from Redux:', {
      projectName,
      alphaCode,
      selectedClient,
    });
  };

  const handleClientChange = (item: ISelectItem | null) => {
    dispatch(setServerSelected({type: 'project', selected: []}));
    dispatch(setServerSelected({type: 'load', selected: []}));
    dispatch(
      setServerSelected({
        type: 'production',
        selected: [],
      })
    );
    const selectedValue = item?.displayValue || '';
    const selectedKey = item ? Number(item.key) : null;
    dispatch(setAlphaCode(''));
    setValue('alphaCode', '');
    dispatch(setSelectedClient(selectedValue.toString()));
    dispatch(setSelectedClientId(selectedKey));

    if (selectedValue) {
      const folderPath = `${selectedValue}\\${projectName}`;

      dispatch(setFolderPaths({type: 'project', path: folderPath}));
      dispatch(setFolderPaths({type: 'ingestion', path: folderPath}));
      dispatch(setFolderPaths({type: 'production', path: folderPath}));
      setNameChanged(false);
    }
  };

  useEffect(() => {
    const subscription = watch((value) => {
      dispatch(setProjectName(value.projectName || ''));
      dispatch(setAlphaCode(value.alphaCode || ''));
    });
    return () => subscription.unsubscribe();
  }, [watch, dispatch]);

  useEffect(() => {
    if (alphaCodeCheck && !isError && enabled) {
      setToastType(ToastType.Error);
      setMessage(t('projectManagement.createProjectModule.alphaCodeError'));
      setShowToast(true);
      clearErrors('alphaCode');
      setTimeout(() => {
        setError('alphaCode', {
          type: 'manual',
          message: '',
        });
      }, 0);
      setEnabled(false);
    }
  }, [alphaCodeCheck, isError, enabled, setError, clearErrors, t]);

  const handleProjectName = (e: React.FocusEvent<HTMLInputElement>) => {
    const trimmedValue = e.target.value.trim();
    if (trimmedValue === '') {
      setValue('projectName', trimmedValue);
      return;
    }

    if (
      projectName !== '' &&
      selectedEntityId &&
      type != 'edit' &&
      nameChanged
    ) {
      const folderPath = `${selectedClient}\\`;
      dispatch(
        setFolderPaths({
          type: 'ingestion',
          path: folderPath.concat(projectName),
        })
      );
      dispatch(
        setFolderPaths({
          type: 'production',
          path: folderPath.concat(projectName),
        })
      );
      dispatch(
        setFolderPaths({
          type: 'project',
          path: folderPath.concat(projectName),
        })
      );
      setNameChanged(false);
    }
  };

  const {onChange, ...rest} = register('projectName', {
    required: `${t('projectManagement.createProjectModule.projectNameRequired')}`,

    maxLength: {
      value: 50,
      message: `${t('projectManagement.createProjectModule.exceed50Character')}`,
    },
    pattern: {
      value: /^[A-Za-z0-9 _-]+$/,
      message: t(
        'projectManagement.createProjectModule.specialCharacterValidation'
      ),
    },
  });
  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box className="project-header-container title-tooltip">
        <Typography
          className="typo-text headline3_size"
          data-testid={`create-project-header-typography`}
        >
          {type !== 'edit'
            ? t('projectManagement.createProject')
            : t('projectManagement.editProject.title')}
        </Typography>
        {type === 'edit' && (
          <ArrTooltipWrapper
            title={
              <Typography
                className="typo-text headline3_size"
                data-testid={`create-project-header-title`}
              >
                {t('projectManagement.editProject.title')}
              </Typography>
            }
            trigger={
              <IconButton aria-label="Info" sx={{padding: 0}}>
                <InfoIcon fontSize="small" className="info-icon" />
              </IconButton>
            }
          >
            <EditProjectTooltip />
          </ArrTooltipWrapper>
        )}
      </Box>
      <Box className="project-header-container project-header-container-fields">
        <Box
          className={`project-header-dropdown-container ${roleId === -2 && 'domain-dropdown-container'}`}
        >
          <Box className="project-name">
            <ArrTextField
              placeholder={t(
                'projectManagement.createProjectModule.projectName'
              )}
              id="project-name-input"
              label={t('projectManagement.createProjectModule.projectName')}
              required
              {...rest}
              state={errors.projectName ? 'error' : 'enabled'}
              supportText={errors.projectName?.message || ''}
              slotProps={{
                inputLabel: type === 'edit' ? {shrink: true} : {},
              }}
              onChange={(e) => {
                onChange(e);
                setNameChanged(true);
              }}
              onBlur={handleProjectName}
              dataTestId="create-project-name"
            />
          </Box>

          <Box className="client-name">
            <ArrSingleSelectDropdown
              dropdownLabel={t(
                'projectManagement.createProjectModule.clientName'
              )}
              dropdownItems={dropdownItems}
              selectedValues={
                type === 'edit' || roleId === -2
                  ? selectedEntityId
                  : selectedEntity
              }
              onSelectionChange={handleClientChange}
              dropdownItemKey="key"
              dropdownItemDisplay="displayValue"
              disabled={type === 'edit' || roleId === -2}
              searchable
              showNoResultsText
              dataTestId="asd-clientName"
            />
          </Box>
          {roleId === -1 && (
            <Box className="alpha-code">
              <ArrTextField
                placeholder={t(
                  'projectManagement.createProjectModule.alphaCode'
                )}
                id="alpha-code-input"
                dataTestId="create-project-alpha-code"
                label={t('projectManagement.createProjectModule.alphaCode')}
                required
                {...register('alphaCode', {
                  required: t(
                    'projectManagement.createProjectModule.alphaCodeRequired'
                  ),
                  onBlur: handleAlphaBlur,
                  maxLength: {
                    value: 16,
                    message: t(
                      'projectManagement.createProjectModule.alphaCodeMaxLength'
                    ),
                  },
                  pattern: {
                    value: /^\w+$/,
                    message: t(
                      'projectManagement.createProjectModule.specialCharacterValidation'
                    ),
                  },
                })}
                state={errors.alphaCode ? 'error' : 'enabled'}
                supportText={errors.alphaCode?.message || ''}
                slotProps={{
                  inputLabel: type === 'edit' ? {shrink: true} : {},
                  input: {
                    inputProps: {
                      maxLength: 16,
                    },
                  },
                }}
              />
            </Box>
          )}
        </Box>
        {type === 'edit' && roleId === -1 && (
          <Box className="project-guid-container">
            <Box className="project-guid">
              <ArrTextField
                placeholder={t('projectManagement.editProject.projectGuid')}
                id="project-guid"
                label={t('projectManagement.editProject.projectGuid')}
                {...register('projectGuid')}
                state={'inactive'}
                slotProps={{
                  inputLabel: type === 'edit' ? {shrink: true} : {},
                }}
                dataTestId="create-project-guid"
              />
            </Box>
            <Box className="project-guid">
              <ArrTextField
                placeholder={t(
                  'projectManagement.editProject.projectIndexGuid'
                )}
                id="project-index-guid"
                label={t('projectManagement.editProject.projectIndexGuid')}
                {...register('projectIndexGuid')}
                state={'inactive'}
                slotProps={{
                  inputLabel: type === 'edit' ? {shrink: true} : {},
                }}
                dataTestId="create-project-index-guid"
              />
            </Box>
          </Box>
        )}
      </Box>
      {showToast && (
        <ArrToast
          id="status-toast"
          title={
            toastType === ToastType.Success
              ? t('common.success')
              : t('common.error')
          }
          type={toastType}
          message={message}
          duration={10000}
          dismissable
          onClose={() => setShowToast(false)}
          closeIcon={<CloseToast />}
          position="bottom-right"
          className="activation-toast"
        />
      )}
      {isLoading && type !== 'Edit' && (
        <ArrLoadingSpinner isOpen={isLoading} overlay />
      )}
    </Box>
  );
};

export default CreateProjectHeader;
